import React from "react";
import { Box, Paper, Typography, Chip } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { alpha } from "@mui/material/styles";

const ACCENT = {
  primary: "#1976d2",
  success: "#2e7d32",
  warning: "#ed6c02",
  error: "#d32f2f",
  violet: "#6366f1",
};

export default function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  accent = "primary",
  trend,
  onClick,
  children,
}) {
  const color = ACCENT[accent] || ACCENT.primary;
  const trendUp = trend?.direction === "up";
  const trendColor = trendUp ? "#2e7d32" : "#d32f2f";

  const handleKeyDown = (e) => {
    if (!onClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "160px", 
        width: "100%",
        p: 2.5,
        borderRadius: 2,
        border: `1px solid #e2e8f0`,
        bgcolor: "#ffffff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s ease",
        "&:hover": onClick
          ? {
              boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
              borderColor: alpha(color, 0.3),
              transform: "translateY(-2px)",
            }
          : {},
        "&:focus-visible": onClick
          ? { outline: `2px solid ${color}`, outlineOffset: 2 }
          : {},
      }}
    >
      {/* Top section - Title and Icon */}
      <Box sx={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "flex-start", 
        gap: 1.5,
        flexShrink: 0,
      }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ 
            fontSize: 12, 
            fontWeight: 600, 
            color: "#64748b",
            letterSpacing: "0.02em",
            textTransform: "uppercase",
          }}>
            {title}
          </Typography>
        </Box>
        {Icon && (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(color, 0.1),
              color,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </Box>
        )}
      </Box>

      {/* Middle section - Value */}
      <Box sx={{ 
        flex: 1,
        display: "flex",
        alignItems: "center",
        py: 0.5,
      }}>
        <Typography
          component="p"
          sx={{
            fontWeight: 700,
            fontSize: "1.75rem",
            letterSpacing: "-0.03em",
            lineHeight: 1.2,
          }}
        >
          {typeof value === "number" ? value.toLocaleString() : value}
        </Typography>
      </Box>

      {/* Bottom section - Subtitle and Trend */}
      <Box sx={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between", 
        gap: 1,
        flexShrink: 0,
        pt: 1,
        borderTop: `1px solid #f1f5f9`,
      }}>
        {subtitle ? (
          <Typography sx={{ 
            fontSize: 12, 
            color: "#94a3b8",
            lineHeight: 1.4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            {subtitle}
          </Typography>
        ) : <span />}
        {trend && (
          <Chip
            size="small"
            icon={trendUp ? <TrendingUpIcon /> : <TrendingDownIcon />}
            label={`${trendUp ? "+" : "-"}${trend.percent}%`}
            sx={{
              height: 24,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: trendUp ? "#e8f5e9" : "#ffebee",
              color: trendColor,
              "& .MuiChip-icon": { color: trendColor, fontSize: 14 },
            }}
          />
        )}
      </Box>
      {children && (
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid #f1f5f9` }}>{children}</Box>
      )}
    </Paper>
  );
}